import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { getIn, useFormikContext } from 'formik'
import { isObject } from 'lodash'

import { Field, type IFieldLabelBadge } from '../Field'
import { FieldHelperText } from '@ui/elements/FieldHelperText'
import { type IconTypes } from '@ui/elements/Icon'

export interface FormControlRenderProps<ValueType = any> {
  value: ValueType
  initialValue?: ValueType
  handleChange: (value: ValueType) => void
  handleBlur: () => void
  setFieldTouched?: (
    fields: { [field: string]: boolean },
    shouldValidate: boolean
  ) => void
  setFieldError?: (fieldName: string, errorMessage: string) => void
  disabled: boolean
  isSubmitting: boolean
  error: boolean
}

export interface IFormikFormControlProps {
  name: string
  id: string
  layout?: 'horizontal' | 'vertical'
  label?: string | ReactNode
  labelId?: string
  labelFor?: string
  labelIcon?: IconTypes
  labelBadge?: IFieldLabelBadge
  labelTooltipMessage?: string | ReactNode
  labelTooltipAriaLabel?: string
  helperText?: string
  hideHelperText?: boolean
  required?: boolean
  disabled?: boolean
  fullWidth?: boolean
}

export const FormControl = <ValueType = any,>({
  id,
  name,
  layout,
  label,
  labelId,
  labelFor,
  children,
  disabled,
  required,
  labelTooltipMessage,
  labelIcon,
  labelBadge,
  labelTooltipAriaLabel,
  helperText,
  hideHelperText,
}: IFormikFormControlProps & {
  children: (args: FormControlRenderProps<ValueType>) => ReactNode
}) => {
  const {
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    setTouched,
    setFieldError,
    values,
    touched,
    errors,
    initialValues,
  } = useFormikContext()

  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleChange = useCallback(
    (value) => {
      // setFieldValue actually returns a Promise, but the type definition incorrectly specifies it returns void.
      // Therefore, we need to cast it to the correct type.
      // See: https://github.com/jaredpalmer/formik/issues/2059
      ;(
        setFieldValue as (
          field: string,
          value: any,
          shouldValidate?: boolean
        ) => Promise<void>
      )(name, value).then(() => {
        // Only update if component is still mounted
        if (isMountedRef.current) {
          setFieldTouched(name, true)
        }
      })
    },
    [name, setFieldValue, setFieldTouched]
  )

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true)
  }, [name, setFieldTouched])

  const fieldError = getIn(errors, name)
  const errorMessages = !isObject(fieldError)
    ? {
        [name]: fieldError,
      }
    : fieldError
  const hasError = getIn(touched, name) && !!fieldError

  const formikValue = getIn(values, name)

  // Helper text
  const helperMessages = !isObject(helperText)
    ? {
        [name]: helperText,
      }
    : helperText

  const formHelperText = hasError ? errorMessages : helperMessages
  const formHelperTextId = helperText && id ? `${id}-helper-text` : undefined
  const hasMessages = !hideHelperText
    ? Object.values(formHelperText).some((o) => !!o)
    : false

  return (
    <Field
      layout={layout}
      label={label}
      labelId={labelId}
      labelFor={labelFor || id}
      error={hasError}
      required={required}
      labelIcon={labelIcon}
      labelBadge={labelBadge}
      labelTooltipMessage={labelTooltipMessage}
      labelTooltipAriaLabel={labelTooltipAriaLabel}
    >
      {children({
        value: formikValue,
        handleChange,
        handleBlur,
        disabled,
        isSubmitting,
        error: hasError,
        setFieldTouched: setTouched,
        setFieldError,
        initialValue: initialValues[name],
      })}
      {hasMessages &&
        Object.entries(formHelperText).map(([key, error]) => (
          <FieldHelperText key={key} id={formHelperTextId} error={hasError}>
            {error}
          </FieldHelperText>
        ))}
    </Field>
  )
}
